import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms/input/input';
import { sanitizeError } from '@/lib/utils/errors';

import { useAuthInfo } from '../../allauth_auth/hooks';
import FormErrors, { hasErrors } from '../../components/FormErrors';
import * as allauth from '../../lib/allauth';
import AuthenticateFlow from './AuthenticateFlow';

interface AuthenticateCodeProps {
  authenticatorType: string;
  children?: React.ReactNode;
}

interface ResponseContent {
  errors?: Array<{ param?: string; message: string }>;
}

export default function AuthenticateCode(props: AuthenticateCodeProps) {
  const [code, setCode] = useState('');
  const [response, setResponse] = useState<{
    fetching: boolean;
    content: ResponseContent | null;
  }>({ fetching: false, content: null });
  const authInfo = useAuthInfo();

  if (authInfo?.pendingFlow?.id !== allauth.Flows.MFA_AUTHENTICATE) {
    return <Navigate to='/' />;
  }

  function submit() {
    setResponse({ ...response, fetching: true });
    allauth
      .mfaAuthenticate(code)
      .then(content => {
        setResponse(r => {
          return { ...r, content };
        });
      })
      .catch(e => {
        console.error(e);
        toast.error(sanitizeError(e));
      })
      .then(() => {
        setResponse(r => {
          return { ...r, fetching: false };
        });
      });
  }
  return (
    <AuthenticateFlow authenticatorType={props.authenticatorType}>
      {props.children}
      <div className='space-y-1.5'>
        <Input
          label='Authentication code'
          type='text'
          id='code'
          inputClassName='text-center text-lg font-mono tracking-widest'
          placeholder='000000'
          isRequired
          onChange={setCode}
          value={code}
          name='code'
          maxLength={6}
          autoComplete='one-time-code'
          size='md'
          isInvalid={hasErrors({
            errors: response.content?.errors,
            param: 'code',
          })}
        />
        <FormErrors param='code' errors={response.content?.errors} />
        <p className='text-xs text-gray-500 dark:text-gray-400 text-center'>
          Enter the 6-digit code from your authenticator app
        </p>
      </div>
      <div className='mt-6'>
        <Button
          onClick={() => submit()}
          disabled={response.fetching || code.length < 6}
          className='w-full'
          isLoading={response.fetching}
          showTextWhileLoading
        >
          {response.fetching ? 'Verifying…' : 'Verify and sign in'}
        </Button>
      </div>
    </AuthenticateFlow>
  );
}
